import React, { useState, useContext, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-apollo-hooks';
import { loader } from 'graphql.macro';

import { withGraphQLErrors, ProfileContext } from '@loyalup/core';
import { WithTitle, Checkbox, Button, Field, Form, FileUploader, ImageCrop, useNotifications } from '@loyalup/ui';
import updateProfileInCache from 'utils/updateProfileInCache';

import s from './UserProfile.module.css';

const UPDATE_PROFILE_MUTATION = loader('../../../gql/mutations/UpdateMyProfile.gql');
const CREATE_USERPIC_MUTATION = loader('../../../gql/mutations/CreateUserpic.gql');
const SET_USERPIC_VIEWPORT_MUTATION = loader('../../../gql/mutations/SetUserpicViewport.gql');

const langItems = [
  {
    value: 'ru',
    label: 'Русский',
  },
  {
    value: 'en',
    label: 'English',
  },
];

const UserProfile = () => {
  const [t, i18n] = useTranslation();
  const { user } = useContext(ProfileContext);
  const uploadRef = useRef();

  const [sendNotification] = useNotifications();

  const [profile, setProfile] = useState({
    firstName: user.firstName,
    lastName: user.lastName || '',
    phone: user.phone || '',
    newsEmailsEnabled: user.newsEmailsEnabled,
    locale: i18n.languages[0],
  });

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageCropping, setImageCropping] = useState(false);
  const [errors, setErrors] = useState({});
  const [userpicImage, setUserpicImage] = useState();

  const updateProfileCache = {
    update: (cache, { data: { updateMyProfile } }) => {
      updateProfileInCache(cache, { user: { ...user, ...updateMyProfile } });
    },
  };

  const [updateProfile] = useMutation(UPDATE_PROFILE_MUTATION, updateProfileCache);
  const [createUserpic] = useMutation(CREATE_USERPIC_MUTATION);
  const [setUserpicViewport] = useMutation(SET_USERPIC_VIEWPORT_MUTATION);

  const onProfileChange = async () => {
    setLoading(true);
    setErrors({});
    const fields = {
      ...profile,
      userpicImageId: userpicImage?.id,
      locale: profile.locale.toUpperCase(),
    };
    const { errors } = await withGraphQLErrors(() =>
      updateProfile({
        variables: fields,
      }),
    );

    setLoading(false);
    if (errors) {
      setErrors(errors);
    } else {
      sendNotification({ text: t('Сохранено') });
    }
  };

  const onUpdateLocale = async locale => {
    setLoading(true);
    setErrors({});
    const { errors } = await withGraphQLErrors(() =>
      updateProfile({
        variables: { locale: locale.toUpperCase() },
      }),
    );

    setLoading(false);
    if (errors) {
      setErrors(errors);
    } else {
      sendNotification({ text: t('Сохранены настройки языка') });
    }
  };

  useEffect(() => {
    setUserpicImage(user.userpicImage);
  }, [user.userpicImage]);

  const onImageUpload = async path => {
    const { data } = await createUserpic({ variables: { path } });
    setUserpicImage(data.createImage);
    setUploading(false);
    setImageCropping(true);
  };

  return (
    <WithTitle title={t('Мой Профиль')}>
      <div className={s.header}>
        <h1>{t('Мой Профиль')}</h1>
      </div>
      <Form className={s.content} onSubmit={onProfileChange} noContainers>
        <main className={s.main}>
          <div className={s.horizontal}>
            <div className={s.avatar}>
              {userpicImage && <img alt="avatar" src={userpicImage.url400} />}
              {!userpicImage && (
                <div className={s.userpicPlaceholder}>{t(uploading ? 'Загрузка...' : 'Загрузите свою фотографию')}</div>
              )}
              <Button
                theme={userpicImage ? 'toolbar' : 'toolbar-transparent'}
                type="button"
                onClick={() => uploadRef.current.selectFile()}
                className={s.upload}
                loading={uploading}
              >
                {t(userpicImage ? 'Заменить' : 'Выбрать файл')}
              </Button>
            </div>
            <div className={s.form}>
              <div className={s.emailContainer}>
                <span className={s.title}>{t('Е-мейл')}</span>
                <span className={s.value}>{user?.email}</span>
              </div>
              <Field
                type="text"
                className={s.field}
                onChange={firstName => setProfile({ ...profile, firstName })}
                value={profile.firstName}
                error={errors.firstName}
                label={t('Имя')}
                disabled={loading}
              />
              <Field
                type="text"
                className={s.field}
                onChange={lastName => setProfile({ ...profile, lastName })}
                value={profile.lastName}
                error={errors.lastName}
                label={t('Фамилия')}
                disabled={loading}
              />
              <Field
                type="phone"
                className={s.field}
                onChange={phone => setProfile({ ...profile, phone })}
                value={profile.phone}
                error={errors.phone}
                label={t('Телефон')}
                disabled={loading}
              />
              <Field
                type="select"
                className={s.field}
                items={langItems}
                value={profile.locale}
                error={errors.locale}
                label={t('Язык')}
                disabled={loading}
                onChange={locale => {
                  i18n.changeLanguage(locale);
                  setProfile({ ...profile, locale });
                  onUpdateLocale(locale);
                }}
                stretch
              />
              <Checkbox
                className={s.field}
                checked={profile.newsEmailsEnabled}
                onChange={newsEmailsEnabled => setProfile({ ...profile, newsEmailsEnabled })}
                disabled={loading}
              >
                {t('Получать новости и полезные статьи')}
              </Checkbox>
            </div>
          </div>
        </main>
        <footer className={s.footer}>
          <Button type="submit" fixedWidth={175} loading={loading} center>
            {t('Сохранить')}
          </Button>
        </footer>
      </Form>
      <FileUploader
        hidden
        accept="image/*"
        type="userpic"
        server={process.env.REACT_APP_SERVER_URL}
        onStart={() => setUploading(true)}
        onFinish={({ path }) => onImageUpload(path)}
        ref={uploadRef}
      />
      {userpicImage && (
        <ImageCrop
          image={userpicImage.rawUrl}
          isOpen={imageCropping}
          onFinish={async viewport => {
            setImageCropping(false);
            if (viewport) {
              const { data } = await setUserpicViewport({ variables: { imageId: userpicImage.id, viewport } });
              setUserpicImage(data.setImageViewport);
            }
          }}
          aspectRatio={1}
          round
        />
      )}
    </WithTitle>
  );
};

export default UserProfile;
